/** "Compare" button with a keyboard-operable menu of comparison targets. */
import React, { useEffect, useRef, useState } from 'react';

export interface CompareMenuOption { key: string; label: string }

const CompareMenu: React.FC<{ options: CompareMenuOption[]; onChoose: (key: string) => void }> = ({ options, onChoose }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<Array<HTMLLIElement | null>>([]);

  useEffect(() => {
    if (!open) return;
    itemRefs.current[0]?.focus();
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const choose = (key: string) => {
    setOpen(false);
    onChoose(key);
  };

  const onItemKeyDown = (e: React.KeyboardEvent, i: number) => {
    const n = options.length;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      itemRefs.current[(i + 1) % n]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      itemRefs.current[(i - 1 + n) % n]?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      choose(options[i]!.key);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    }
  };

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        className="btn-secondary"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        Compare
      </button>
      {open && (
        <ul role="menu" className="absolute right-0 z-50 mt-1 min-w-full rounded-ctl border border-line-input bg-white py-1 shadow-lg">
          {options.map((o, i) => (
            <li
              key={o.key}
              role="menuitem"
              tabIndex={-1}
              ref={(el) => { itemRefs.current[i] = el; }}
              className="cursor-pointer whitespace-nowrap px-3 py-1.5 text-sm text-ink hover:bg-slate-50 focus:bg-slate-50 focus:outline-none"
              onClick={() => choose(o.key)}
              onKeyDown={(e) => onItemKeyDown(e, i)}
            >
              {o.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CompareMenu;
